import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
	Activity,
	BagPlusFill,
	Box2HeartFill,
	PhoneVibrateFill,
	PinMapFill,
	PlusCircleFill,
	SendFill,
} from 'react-bootstrap-icons';

import useTranslation from '../../hooks/use_translation';
import BtnIcon from '../framework/buttons/btn_icon';
import Link from '../framework/link';
import SectionHeader from '../framework/sections/section-header';
import Trans from '../translation/trans';

const HowItWorks = ({ enableReadMore, hideTitle, type = 'blood_sample' }) => {
	const { t } = useTranslation('component_how_it_works');
	const types = Array.isArray(type) ? type : [type];
	const [activeType, setActiveType] = useState(types[0]);

	const readMoreLinks = {
		blood_sample: '',
		ecg: 'langtids-ekg',
		mri: 'rontgen',
	};

	const cardDataMap = {
		blood_sample: [
			{
				btnLabel: t('t9'),
				btnLink: '/tester/',
				btnTarget: '_self',
				description: t('t4'),
				icon: <BagPlusFill />,
				title: t('t3'),
			},
			{
				btnLabel: t('t14'),
				btnLink: 'https://app.zample.com',
				btnTarget: '_blank',
				description: t('t13'),
				icon: <PlusCircleFill />,
				title: t('t12'),
			},
			{
				btnLabel: t('t10'),
				btnLink: '/mottagningar/',
				btnTarget: '_self',
				description: t('t6'),
				icon: <PinMapFill />,
				title: t('t5'),
			},
			{
				btnLabel: <Trans i18nKey="t11" t={t} />,
				btnLink: '#',
				btnTarget: '_self',
				description: t('t8'),
				icon: <PhoneVibrateFill />,
				isDivLink: true,
				title: t('t7'),
			},
		],
		ecg: [
			{
				description: t('t29'),
				icon: <BagPlusFill />,
				title: t('t30'),
			},
			{
				description: t('t31'),
				icon: <PlusCircleFill />,
				title: t('t32'),
			},
			{
				description: t('t33'),
				icon: <Box2HeartFill />,
				title: t('t34'),
			},
			{
				description: t('t35'),
				icon: <Activity />,
				title: t('t36'),
			},
			{
				description: t('t37'),
				icon: <SendFill />,
				title: t('t38'),
			},
			{
				description: t('t40'),
				icon: <PhoneVibrateFill />,
				title: t('t41'),
			},
		],
		mri: [
			{
				description: t('t18'),
				icon: <BagPlusFill />,
				title: t('t19'),
			},
			{
				description: t('t21'),
				icon: <PlusCircleFill />,
				title: t('t22'),
			},
			{
				description: t('t23'),
				icon: <PinMapFill />,
				title: t('t24'),
			},
			{
				description: t('t25'),
				icon: <PhoneVibrateFill />,
				title: t('t26'),
			},
		],
	};

	const sectionHeaderMap = {
		blood_sample: {
			subTitle: t('t1'),
			title: t('t2'),
		},
		ecg: {
			subTitle: t('t27'),
			title: t('t28'),
		},
		mri: {
			subTitle: t('t27'),
			title: t('t16'),
		},
	};

	const tabsMap = {
		blood_sample: { label: t('t42') },
		ecg: { label: t('t43') },
		mri: { label: t('t44') },
	};

	const cardData = cardDataMap[activeType] || [];
	const sectionHeader = sectionHeaderMap[activeType] || {};
	const readMoreLink = readMoreLinks[activeType] || '';

	return (
		<div className="container">
			{!hideTitle && (
				<SectionHeader
					align="center"
					headingType="p"
					hideButton={true}
					size="lg"
					subHeadingType="h3"
					subTitle={sectionHeader.subTitle}
					title={sectionHeader.title}
				/>
			)}

			{types.length > 1 && (
				<ul className="nav nav-underline nav-fill justify-content-center mb-4">
					{types.map((tabType) => (
						<li className="nav-item" key={tabType}>
							<button
								className={`nav-link ${tabType === activeType ? 'active' : ''}`}
								onClick={() => setActiveType(tabType)}
							>
								{tabsMap[tabType]?.label || tabType}
							</button>
						</li>
					))}
				</ul>
			)}

			<div className="row align-items-start justify-content-between mt-4">
				{cardData.map((card, index) => {
					const stepLabel = card.step || `${t('t15')} ${index + 1}`;
					return (
						<div className="col-md-6 mb-3 mb-lg-4" key={index}>
							<div className="card rounded-3 border-0 bg-gray-100 how-it-works-card">
								<div className="card-body d-flex flex-column">
									<div
										className={`step-label d-flex align-items-center mb-4 gap-4 ${activeType === 'mri' ? 'text-blue-600' : 'text-primary'}`}
									>
										{card.icon && <div className="icon-md">{card.icon}</div>}
										<div className="fw-700">{stepLabel}</div>
									</div>
									<div className="mt-auto">
										<h4 className="title-small mb-2">{card.title}</h4>
										<p className="small text-muted">{card.description}</p>
									</div>
									{card.btnLabel && (
										<BtnIcon
											className="text-decoration-none"
											iconSize="md"
											labelBefore={card.btnLabel}
											labelSize="sm"
											linkType={card.isDivLink ? 'div' : undefined}
											target={card.btnTarget}
											to={card.btnLink}
											variant="show_more_transparent"
										/>
									)}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{enableReadMore && (
				<div className="d-flex justify-content-center mt-4">
					<Link
						className="text-decoration-none btn-lg btn btn-outline-dark rounded-pill"
						iconSize="md"
						labelSize="sm"
						linkType="div"
						to={`/sa-fungerar-det/${readMoreLink}`}
					>
						{t('t39')}
					</Link>
				</div>
			)}
		</div>
	);
};

HowItWorks.propTypes = {
	enableReadMore: PropTypes.bool,
	hideTitle: PropTypes.bool,
	type: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
	]),
};

export default HowItWorks;
